import { companiesApi, triggerBrowserDownload } from '@/lib/api';
import {
  type ImportedWorkbook,
  type Noyau,
  SETUP_IMPORT_MAX_BYTES,
  type SetupImportOutcome,
  type SingularityLevier,
  importSetupFromClipboard,
  importSetupFromWorkbook,
} from '@mss_creator/shared';
import { ActionBar, Button, Dialog, DialogClose, Tabs } from '@pms-digital/design-system';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { SetupImportFailure } from './setup-import-failure';
import { SetupImportFileTab } from './setup-import-file-tab';
import {
  type SetupImportProblem,
  describeUploadFailure,
  plural,
  problemBeforeUpload,
  problemFromUploadError,
} from './setup-import-messages';
import { SetupImportPasteTab } from './setup-import-paste-tab';
import { SetupImportPreviewTable } from './setup-import-preview-table';
import { SetupImportRefusal } from './setup-import-refusal';
import { SetupImportSourceBar } from './setup-import-source-bar';
import { SetupImportSummary } from './setup-import-summary';
import { SetupImportWarnings } from './setup-import-warnings';

export interface AppliedSetupImport {
  merged: SingularityLevier[];
  writtenCount: number;
  /** File name, or « les cellules collées » — completes the page banner. */
  sourceLabel: string;
}

export interface SetupImportFlowProps {
  onOpenChange: (open: boolean) => void;
  companyId: string;
  /** The page's CURRENT draft: the merge baseline (spec rule 6). */
  draft: SingularityLevier[];
  noyau: Noyau;
  onApply: (applied: AppliedSetupImport) => void;
}

type TabKey = 'paste' | 'file';

/** `null` while on the source tabs; otherwise what the preview area shows. */
type Result =
  | { kind: 'paste' }
  | {
      kind: 'file';
      fileName: string;
      workbook: ImportedWorkbook;
      sheetName: string | undefined;
    }
  | { kind: 'file-problem'; fileName: string; problem: SetupImportProblem }
  | { kind: 'file-failure'; file: File; message: string };

const PASTE_SOURCE = 'Cellules collées';

/**
 * The import dialog's content (boards 02 to 04): the source tabs, then the
 * preview or the refusal. All parsing and merging is the shared module's;
 * this component only chooses the source and renders the outcome. Mounted
 * only while the dialog is open, so closing it (cross, Escape, « Annuler »)
 * drops every bit of state and applies nothing.
 */
export function SetupImportFlow({
  onOpenChange,
  companyId,
  draft,
  noyau,
  onApply,
}: SetupImportFlowProps) {
  const [tab, setTab] = useState<TabKey>('paste');
  const [pasteText, setPasteText] = useState('');
  const [result, setResult] = useState<Result | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => companiesApi.importSingularitySetupFile(companyId, file),
  });
  const template = useMutation({
    mutationFn: () => companiesApi.downloadSingularitySetupTemplate(companyId),
    onSuccess: ({ blob, filename }) => triggerBrowserDownload(blob, filename),
  });

  const pasteOutcome = useMemo<SetupImportOutcome | null>(
    () => (result?.kind === 'paste' ? importSetupFromClipboard(pasteText, draft) : null),
    [result, pasteText, draft],
  );
  const fileOutcome = useMemo(
    () =>
      result?.kind === 'file'
        ? importSetupFromWorkbook(result.workbook, draft, result.sheetName)
        : null,
    [result, draft],
  );
  const outcome: SetupImportOutcome | null = fileOutcome ?? pasteOutcome;
  const preview = outcome?.ok ? outcome.preview : null;
  let problem: SetupImportProblem | null = null;
  if (outcome && !outcome.ok) problem = { kind: 'refusal', refusal: outcome.refusal };
  if (result?.kind === 'file-problem') problem = result.problem;

  const writtenCount = preview?.written.length ?? 0;
  const canApply = preview !== null && writtenCount > 0;

  function handlePaste(text: string, pasted: boolean) {
    setPasteText(text);
    if (text.trim().length === 0) return;
    // A paste always shows its outcome, refusal included; typing only moves
    // on once a header row is recognized, so a half-typed line is not refused.
    if (pasted || importSetupFromClipboard(text, draft).ok) setResult({ kind: 'paste' });
  }

  function handleFile(file: File) {
    const early = problemBeforeUpload(file, SETUP_IMPORT_MAX_BYTES);
    if (early) {
      setResult({ kind: 'file-problem', fileName: file.name, problem: early });
      return;
    }
    upload.mutate(file, {
      // Functional updates: a result the user already moved on from wins.
      onSuccess: (workbook) =>
        setResult(
          (previous) =>
            previous ?? {
              kind: 'file',
              fileName: workbook.fileName || file.name,
              workbook,
              sheetName: undefined,
            },
        ),
      onError: (error) => {
        const refused = problemFromUploadError(error, file);
        setResult(
          (previous) =>
            previous ??
            (refused
              ? { kind: 'file-problem', fileName: file.name, problem: refused }
              : { kind: 'file-failure', file, message: describeUploadFailure(error) }),
        );
      },
    });
  }

  function changeSource() {
    upload.reset();
    setResult(null);
  }

  function retry(file: File) {
    setResult(null);
    setTab('file');
    handleFile(file);
  }

  function apply() {
    if (!preview || !canApply) return;
    onApply({
      merged: preview.merged,
      writtenCount,
      sourceLabel: result?.kind === 'file' ? result.fileName : 'les cellules collées',
    });
  }

  let description: string | null = null;
  if (result === null) {
    description =
      "La première ligne doit contenir les en-têtes (Driver, Levier de valeur, …). Les colonnes absentes ne bloquent pas, les cellules vides n'effacent rien.";
  } else if (preview) {
    description = "Vérifiez le résultat : rien n'est enregistré avant « Enregistrer » sur la page.";
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title="Importer le setup depuis Excel"
      {...(description !== null ? { description } : {})}
      size={preview ? 'form' : 'default'}
      actions={
        <ActionBar
          secondary={
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button variant="secondary" type="button" disabled={!canApply} onClick={apply}>
              {canApply
                ? `Appliquer (${plural(writtenCount, 'cellule', 'cellules')})`
                : 'Appliquer'}
            </Button>
          }
        />
      }
    >
      {result === null ? (
        <Tabs
          label="Source du setup"
          value={tab}
          onValueChange={(value) => setTab(value === 'file' ? 'file' : 'paste')}
          items={[
            {
              value: 'paste',
              label: 'Coller des cellules',
              content: <SetupImportPasteTab value={pasteText} onChange={handlePaste} />,
            },
            {
              value: 'file',
              label: 'Importer un fichier',
              content: (
                <SetupImportFileTab
                  onFile={handleFile}
                  readingFileName={upload.isPending ? (upload.variables?.name ?? '') : null}
                />
              ),
            },
          ]}
        />
      ) : (
        <>
          <SetupImportSourceBar
            label={result.kind === 'paste' ? PASTE_SOURCE : sourceFileName(result)}
            candidateSheets={fileOutcome?.candidateSheets ?? []}
            sheetName={fileOutcome?.sheetName ?? null}
            onSheetChange={(sheetName) =>
              setResult((previous) =>
                previous?.kind === 'file' ? { ...previous, sheetName } : previous,
              )
            }
            onChangeSource={changeSource}
          />
          {result.kind === 'file-failure' && (
            <SetupImportFailure message={result.message} onRetry={() => retry(result.file)} />
          )}
          {problem && <SetupImportRefusal problem={problem} />}
          {preview && (
            <>
              <SetupImportSummary preview={preview} />
              <SetupImportWarnings preview={preview} />
              <SetupImportPreviewTable preview={preview} noyau={noyau} />
            </>
          )}
        </>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          icon="download"
          disabled={template.isPending}
          onClick={() => template.mutate()}
        >
          Télécharger le modèle (setup enregistré)
        </Button>
        {template.isError && (
          <span className="text-caption text-danger">Le téléchargement du modèle a échoué.</span>
        )}
      </div>
    </Dialog>
  );
}

function sourceFileName(result: Exclude<Result, { kind: 'paste' }>): string {
  return result.kind === 'file-failure' ? result.file.name : result.fileName;
}
